import { useCallback, useMemo, memo } from "react";
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Typography,
  IconButton,
  Autocomplete,
  Tooltip,
} from "@mui/material";
import { DeleteOutline as DeleteIcon } from "@mui/icons-material";
import { getOperatorsForType, type FilterRuleRow, type ItsColumnDef } from "./constants";

type FilterRuleProps = {
  rule: FilterRuleRow;
  onUpdate: (rule: FilterRuleRow) => void;
  onDelete: () => void;
  availableColumns: ItsColumnDef[];
};

const FilterRule = memo(({ rule, onUpdate, onDelete, availableColumns }: FilterRuleProps) => {
  const column = useMemo(
    () => availableColumns.find((c) => c.id === rule.field) ?? availableColumns[0],
    [availableColumns, rule.field]
  );

  const operators = useMemo(() => getOperatorsForType(column?.type || "string"), [column?.type]);

  const needsValue = useMemo(
    () => !["isEmpty", "isNotEmpty"].includes(rule.operator),
    [rule.operator]
  );

  const isMultiValue = useMemo(() => ["in", "notIn"].includes(rule.operator), [rule.operator]);

  const handleFieldChange = useCallback(
    (fieldId: string) => {
      const newColumn = availableColumns.find((c) => c.id === fieldId) ?? availableColumns[0];
      const newOperators = getOperatorsForType(newColumn.type);
      onUpdate({
        ...rule,
        field: fieldId,
        operator: newOperators[0].id,
        value: "",
      });
    },
    [availableColumns, rule, onUpdate]
  );

  const handleOperatorChange = useCallback(
    (operatorId: string) => {
      onUpdate({
        ...rule,
        operator: operatorId,
        value: operatorId === "isEmpty" || operatorId === "isNotEmpty" ? "" : rule.value,
      });
    },
    [rule, onUpdate]
  );

  const handleValueChange = useCallback(
    (newValue: string) => {
      onUpdate({
        ...rule,
        value: newValue,
      });
    },
    [rule, onUpdate]
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" },
        gap: 1.25,
        p: 1.25,
        borderRadius: 1.5,
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        transition: "all 0.15s ease",
        "&:hover": {
          borderColor: "primary.main",
          boxShadow: (theme) => theme.shadows[1],
        },
      }}
    >
      {/* Searchable & Categorized Field Selector */}
      <Autocomplete
        size="small"
        options={availableColumns}
        groupBy={(option) => option.category || "General"}
        getOptionLabel={(option) => option.label || option.id}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        value={column}
        onChange={(_, newVal) => {
          if (newVal) handleFieldChange(newVal.id);
        }}
        disableClearable
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Field"
            sx={{
              "& .MuiInputBase-root": {
                fontSize: "0.8125rem",
                py: "2px",
              },
            }}
          />
        )}
        sx={{
          flex: { xs: "1 1 100%", sm: "0 0 35%", md: "0 0 32%" },
          minWidth: 160,
        }}
      />

      {/* Operator Dropdown */}
      <FormControl
        size="small"
        sx={{
          flex: { xs: "1 1 100%", sm: "0 0 25%", md: "0 0 24%" },
          minWidth: 130,
        }}
      >
        <Select
          value={rule.operator}
          onChange={(e) => handleOperatorChange(e.target.value as string)}
          sx={{
            fontSize: "0.8125rem",
            "& .MuiSelect-select": {
              py: 0.85,
              fontSize: "0.8125rem",
            },
          }}
        >
          {operators.map((op) => (
            <MenuItem key={op.id} value={op.id} sx={{ fontSize: "0.8125rem" }}>
              {op.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Value Input or 'No value needed' placeholder */}
      {needsValue ? (
        <TextField
          size="small"
          placeholder={isMultiValue ? "Values (comma-separated, e.g. A, B)" : "Enter value"}
          value={rule.value || ""}
          onChange={(e) => handleValueChange(e.target.value)}
          sx={{
            flex: { xs: "1 1 100%", sm: 1 },
            minWidth: 140,
            "& .MuiInputBase-root": {
              fontSize: "0.8125rem",
            },
            "& .MuiInputBase-input": {
              py: 0.85,
            },
          }}
        />
      ) : (
        <Box
          sx={{
            flex: { xs: "1 1 100%", sm: 1 },
            py: 0.75,
            px: 1.25,
            bgcolor: "action.hover",
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            minHeight: 36,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
            No value required ({rule.operator === "isEmpty" ? "empty" : "not empty"})
          </Typography>
        </Box>
      )}

      {/* Delete Rule Action */}
      <Tooltip title="Remove condition">
        <IconButton
          color="error"
          size="small"
          onClick={onDelete}
          sx={{
            alignSelf: { xs: "flex-end", sm: "center" },
            width: 32,
            height: 32,
            borderRadius: 1,
            flexShrink: 0,
            "&:hover": { bgcolor: "error.lighter" },
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
});

FilterRule.displayName = "FilterRule";

export default FilterRule;
